<template>
    <div
        v-if="canViewNotifications"
        class="relative inline-block"
        @mouseenter="hovering = true"
        @mouseleave="hovering = false"
    >
        <button class="bell-trigger" @click="toggleShow">
            <BellIcon class="w-5 h-5" />
            <span v-if="notificationNumber" class="bell-badge">
                {{ notificationNumber > 9 ? "9+" : notificationNumber }}
            </span>
        </button>

        <transition name="panel">
            <div
                v-if="show"
                class="notification-panel shadow-xl"
                @mouseenter="hovering = true"
                @mouseleave="handleMouseLeave"
            >
                <div class="panel-header">
                    <span class="font-semibold text-sm text-white">
                        Notificaciones
                    </span>
                    <span v-if="notificationNumber" class="unread-chip">
                        {{ notificationNumber }}
                        {{ notificationNumber === 1 ? "nueva" : "nuevas" }}
                    </span>
                </div>

                <div v-if="isLoading" class="panel-empty">
                    <div class="spinner"></div>
                    <span class="text-theme-muted text-xs mt-2">
                        Cargando...
                    </span>
                </div>

                <div v-else-if="notifications.length" class="notification-list">
                    <div
                        v-for="(value, index) in notifications"
                        :key="index"
                        class="notification-item cursor-pointer"
                        :class="{ 'is-unread': !value.checkedAt }"
                        @click="routePush(value.id, value.metadata.url)"
                    >
                        <div
                            class="unread-bar"
                            :class="value.checkedAt ? 'read' : 'unread'"
                        ></div>
                        <div class="item-body">
                            <div
                                class="flex items-start justify-between gap-2 mb-1"
                            >
                                <span
                                    class="text-sm leading-snug text-theme-base"
                                    :class="
                                        !value.checkedAt
                                            ? 'font-semibold'
                                            : 'font-normal'
                                    "
                                >
                                    {{ value.metadata.title }}
                                </span>
                                <span
                                    class="text-xs text-theme-disabled whitespace-nowrap flex-shrink-0 mt-px"
                                >
                                    {{ value.metadata.date }}
                                </span>
                            </div>
                            <p class="item-message text-theme-muted">
                                {{ value.metadata.message }}
                            </p>
                        </div>
                    </div>
                </div>

                <div v-else class="panel-empty">
                    <BellOffIcon class="w-8 h-8 text-theme-disabled mb-2" />
                    <span class="text-theme-muted text-sm">
                        Sin notificaciones
                    </span>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";

import BellIcon from "@/components/icons/BellIcon.vue";
import BellOffIcon from "@/components/icons/BellOffIcon.vue";

export default {
    name: "BellNotifications",
    components: {
        BellIcon,
        BellOffIcon
    },
    data: () => ({
        show: false,
        hovering: false,
        isLoading: false
    }),
    computed: {
        ...mapState("notifications", ["notifications"]),
        ...mapState("auth", ["tenantId"]),
        canViewNotifications() {
            return (
                !!this.tenantId &&
                this.$ability.hasPermission("notification:List")
            );
        },
        notificationNumber() {
            return this.notifications.filter((n) => !n.checkedAt).length;
        }
    },
    watch: {
        hovering(val) {
            if (!val) {
                setTimeout(() => {
                    if (!this.hovering) this.show = false;
                }, 150);
            }
        }
    },
    methods: {
        ...mapActions("notifications", [
            "verifyNotification",
            "listNotifications"
        ]),
        async routePush(id, path) {
            this.show = false;
            await Promise.race([
                this.verifyNotification({ id }),
                new Promise((resolve) => {
                    setTimeout(() => {
                        this.$router.push(path);
                        resolve();
                    }, 400);
                })
            ]);
        },
        async toggleShow() {
            this.show = !this.show;
            if (this.show) {
                this.isLoading = true;
                try {
                    await this.listNotifications({ required: true });
                } catch (e) {
                    // silent
                }
                this.isLoading = false;
            }
        },
        handleMouseLeave() {
            this.hovering = false;
        }
    }
};
</script>

<style scoped>
.bell-trigger {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: var(--color-theme-base);
    cursor: pointer;
    transition: background-color 0.15s ease;
}

.bell-trigger:hover {
    background-color: var(--color-theme-hover);
}

.bell-badge {
    position: absolute;
    top: 2px;
    right: 2px;
    min-width: 17px;
    height: 17px;
    padding: 0 4px;
    border-radius: 9px;
    background-color: var(--color-stack-secondary);
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    line-height: 17px;
    text-align: center;
}

.notification-panel {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    width: 340px;
    border-radius: 12px;
    overflow: hidden;
    z-index: 1000;
    background-color: var(--color-theme-card);
    border: 1px solid var(--color-theme-border);
}

.panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    background-color: var(--color-stack-primary);
    color: #fff;
}

.unread-chip {
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 20px;
    background-color: var(--color-stack-secondary);
    color: #fff;
}

.notification-list {
    max-height: 340px;
    overflow-y: auto;
}

.notification-item {
    display: flex;
    gap: 0;
    background-color: var(--color-theme-card);
    transition: background-color 0.15s ease;
}

.notification-item:hover {
    background-color: var(--color-theme-hover);
}

.notification-item.is-unread {
    background-color: var(--color-theme-canvas);
}

.unread-bar {
    width: 3px;
    flex-shrink: 0;
    align-self: stretch;
}

.unread-bar.unread {
    background-color: var(--color-info);
}

.unread-bar.read {
    background-color: transparent;
}

.item-body {
    flex: 1;
    padding: 12px 14px;
    min-width: 0;
}

.item-message {
    font-size: 12px;
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin: 0;
}

.panel-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem 1rem;
}

.spinner {
    width: 24px;
    height: 24px;
    border: 2px solid var(--color-theme-border);
    border-top-color: var(--color-theme-primary);
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.panel-enter-active,
.panel-leave-active {
    transition: opacity 0.15s ease, transform 0.15s ease;
}
.panel-enter,
.panel-leave-to {
    opacity: 0;
    transform: translateY(-6px) scale(0.98);
}
</style>
